import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faExternalLinkAlt, faRobot, faSync,
} from '@fortawesome/free-solid-svg-icons';
import { agentsApi } from '../../services/agentsApi';
import OverviewTab from '../../components/agents/OverviewTab';
import TicketsTab from '../../components/agents/TicketsTab';
import ActivityTab from '../../components/agents/ActivityTab';
import PlannerTab from '../../components/agents/PlannerTab';

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
};

type Tab = 'overview' | 'tickets' | 'activity' | 'planner';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'activity', label: 'Activity' },
  { key: 'planner', label: 'Planner' },
];

const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [validation, setValidation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [appUrl, setAppUrl] = useState<string | null>(null);

  const fetchValidation = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await agentsApi.getValidation(id);
      let parsed: any = null;
      if (data?.content?.[0]?.text) {
        try {
          parsed = JSON.parse(data.content[0].text);
        } catch {
          parsed = { raw: data.content[0].text };
        }
      } else {
        parsed = data;
      }
      setValidation(parsed);

      // Try to get app URL
      try {
        const { data: statusData } = await agentsApi.getAppStatus(id);
        if (statusData?.content?.[0]?.text) {
          const statusParsed = JSON.parse(statusData.content[0].text);
          if (statusParsed?.appUrl) setAppUrl(statusParsed.appUrl);
          else if (statusParsed?.url) setAppUrl(statusParsed.url);
        }
      } catch {
        // App status may not be available
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load validation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return null;

  const title = validation?.title || validation?.validation?.title || validation?.raw?.slice(0, 60) || 'Workspace';
  const status = validation?.status || validation?.validation?.status || '';
  const type = validation?.type || validation?.validation?.type || '';
  const statusCls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';

  return (
    <div className="p-6">
      {/* Back + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/consultant/agents')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Agents
        </button>

        {loading ? (
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faSync} className="animate-spin text-indigo-500" />
            <span className="text-gray-500">Loading...</span>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faRobot} className="text-2xl text-indigo-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{title}</h1>
                <div className="flex items-center gap-2 mt-1">
                  {status && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>{status.replace(/_/g, ' ')}</span>}
                  {type && <span className="text-xs text-gray-500">{type.replace(/_/g, ' ')}</span>}
                </div>
              </div>
            </div>
            {appUrl && (
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                Open App
              </a>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab validationId={id} />}
        {activeTab === 'tickets' && <TicketsTab validationId={id} />}
        {activeTab === 'activity' && <ActivityTab validationId={id} />}
        {activeTab === 'planner' && <PlannerTab validationId={id} />}
      </div>
    </div>
  );
};

export default AgentDetail;
